import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Users, Clock, Award } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface HeroSectionProps {
  onCatalogClick?: () => void;
}

export function HeroSection({ onCatalogClick }: HeroSectionProps) {
  const { t } = useTranslation('homepage');

  return (
    <section className="relative bg-gradient-to-r from-amber-50 to-orange-50">
      <div className="h-12 flex-shrink-0"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 lg:pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                {t('features.chefCurated')}
              </Badge>
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                {t('hero.title')}
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                {t('hero.subtitle')}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="bg-amber-900 hover:bg-amber-800 text-white"
                onClick={onCatalogClick}
              >
                {t('hero.buttons.shopBlends')}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-amber-900 text-amber-900 hover:bg-amber-50"
              >
                {t('hero.buttons.customBlend')}
              </Button>
            </div>

            {/* Value Props */}
            <div className="grid grid-cols-3 gap-4 sm:gap-6 pt-8">
              <div className="text-center">
                <Users className="h-8 w-8 text-amber-900 mx-auto mb-2" />
                <p className="font-medium text-gray-900">
                  {t('features.chefCurated')}
                </p>
                <p className="text-sm text-gray-600">
                  {t('features.expertSelections')}
                </p>
              </div>
              <div className="text-center">
                <Award className="h-8 w-8 text-amber-900 mx-auto mb-2" />
                <p className="font-medium text-gray-900">
                  {t('features.premiumQuality')}
                </p>
                <p className="text-sm text-gray-600">
                  {t('features.organicSourced')}
                </p>
              </div>
              <div className="text-center">
                <Clock className="h-8 w-8 text-amber-900 mx-auto mb-2" />
                <p className="font-medium text-gray-900">
                  {t('features.freshDaily')}
                </p>
                <p className="text-sm text-gray-600">
                  {t('features.smallBatches')}
                </p>
              </div>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=400&q=75&fm=webp"
              srcSet="https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=400&q=75&fm=webp 400w,
                      https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=600&q=75&fm=webp 600w,
                      https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=800&q=75&fm=webp 800w"
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 40vw, 384px"
              alt="Premium spices collection"
              className="w-full h-auto rounded-2xl shadow-2xl"
              fetchPriority="high"
              loading="eager"
              decoding="sync"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
          </div>
        </div>
      </div>
    </section>
  );
}
